// redux/slices/remindersSlice.js
import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";
/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  fallback;

const clearTokenOnAuthError = async (error) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch { }
  }
};

const normalizeReminder = (reminder, uid) => ({
  ...reminder,

  uid:
    reminder?.uid ??
    uid,

  orderId:
    reminder?.orderId ??
    reminder?.order_id ??
    null,

  productId:
    reminder?.productId ??
    reminder?.product_id ??
    null,

  productName:
    reminder?.productName ??
    reminder?.product_name ??
    "",

  productImage:
    reminder?.productImage ??
    reminder?.product_image ??
    null,

  clientName:
    reminder?.clientName ??
    reminder?.client_name ??
    "",

  clientPhone:
    reminder?.clientPhone ??
    reminder?.client_phone ??
    "",

  remindAt:
    reminder?.remindAt ??
    reminder?.remind_at ??
    null,

  isSent:
    reminder?.isSent ??
    reminder?.is_sent ??
    false,

  _fetchedForUid:
    String(uid),
});

const syncFlatReminders = (state) => {
  state.reminders = Object.values(state.byUid || {}).flat();
};

const upsertReminder = (items, reminder) => {
  if (!reminder?.id) return items;

  const id = String(reminder.id);
  const index = items.findIndex((item) => String(item?.id) === id);

  if (index === -1) {
    items.push(reminder);
  } else {
    items[index] = {
      ...items[index],
      ...reminder,
    };
  }

  return items;
};

/* ========================= THUNKS ========================= */

// GET /reminders/:uid
export const fetchReminders = createAsyncThunk(
  "reminders/fetchReminders",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) {
        throw new Error("UID не передан");
      }

      const response = await apiRequest(
        "GET",
        `/reminders/${uid}`
      );

      const data = response?.data ?? response;

      let reminders = [];

      if (Array.isArray(data)) {
        reminders = data;
      } else if (Array.isArray(data?.reminders)) {
        reminders = data.reminders;
      } else if (Array.isArray(data?.items)) {
        reminders = data.items;
      } else if (Array.isArray(data?.data)) {
        reminders = data.data;
      } else if (data?.reminder) {
        reminders = [data.reminder];
      }

      return reminders;
    } catch (error) {
      await clearTokenOnAuthError(error);

      console.error(
        "[fetchReminders error]",
        error?.response?.status,
        error?.response?.data || error
      );

      return rejectWithValue(
        getError(
          error,
          "fetchReminders failed"
        )
      );
    }
  }
);

// POST /reminders
// payload: { uid, reminders: [...] } или { uid, reminder: {...} }
export const createReminders = createAsyncThunk(
  "reminders/createReminders",
  async (
    {
      uid,
      reminders,
      reminder,
    },
    {
      dispatch,
      rejectWithValue,
    }
  ) => {
    try {
      if (!uid) {
        throw new Error("UID не передан");
      }

      const items = Array.isArray(reminders)
        ? reminders
        : reminder
          ? [reminder]
          : [];

      if (!items.length) {
        throw new Error("Напоминания не переданы");
      }

      const payload = {
        uid,
        reminders: items.map((item) => ({
          ...item,
          uid: item?.uid ?? uid,
        })),
      };

      const response = await apiRequest(
        "POST",
        "/reminders",
        payload
      );

      const data = response?.data || response || {};
      /*
       * Не полагаемся на формат ответа POST.
       * После успешного создания загружаем
       * фактические данные из базы.
       */
      const fetched = await dispatch(
        fetchReminders(uid)
      ).unwrap();

      return {
        uid,
        reminders: Array.isArray(fetched)
          ? fetched
          : [],
      };
    } catch (error) {
      await clearTokenOnAuthError(error);

      console.error(
        "[createReminders error]",
        error?.response?.status,
        error?.response?.data || error
      );

      return rejectWithValue(
        getError(
          error,
          "createReminders failed"
        )
      );
    }
  }
);
// PUT /reminders/:id
export const updateReminder = createAsyncThunk(
  "reminders/updateReminder",
  async ({ id, patch, reminder, uid }, { dispatch, rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID напоминания не передан");

      const changes = patch || reminder || {};
      const response = await apiRequest("PUT", `/reminders/${id}`, changes);
      const data = response?.data || response;

      return {
        uid,
        reminder: data?.reminder || data,
      };
    } catch (error) {
      await clearTokenOnAuthError(error);

      const message = getError(error, "updateReminder failed");

      return rejectWithValue(message);
    }
  }
);

// DELETE /reminders/:id
export const deleteReminder = createAsyncThunk(
  "reminders/deleteReminder",
  async (payload, { dispatch, rejectWithValue }) => {
    const id = typeof payload === "object" ? payload?.id : payload;
    const uid = typeof payload === "object" ? payload?.uid : undefined;

    try {
      if (!id) throw new Error("ID напоминания не передан");

      const response = await apiRequest("DELETE", `/reminders/${id}`);

      if (response?.status && response.status >= 400) {
        throw new Error(`Ошибка удаления. Код: ${response.status}`);
      }

      return { id, uid };
    } catch (error) {
      await clearTokenOnAuthError(error);

      const message = getError(error, "deleteReminder failed")

      return rejectWithValue(message);
    }
  }
);

export const updateReminderAndRefetch = createAsyncThunk(
  "reminders/updateAndRefetch",
  async ({ id, patch, reminder, uid }, { dispatch }) => {
    await dispatch(updateReminder({ id, patch, reminder, uid })).unwrap();

    if (uid) {
      await dispatch(fetchReminders(uid)).unwrap();
    }

    return { id, uid };
  }
);

export const batchUpdateReminders = createAsyncThunk(
  "reminders/batchUpdate",
  async (updates, { dispatch }) => {
    return Promise.allSettled(
      (updates || []).map((item) => dispatch(updateReminder(item)).unwrap())
    );
  }
);

/* ========================= SELECTORS ========================= */

export const selectRemindersForUid = (state, uid) =>
  state.reminders?.byUid?.[String(uid)] || [];

export const selectAllReminders = (state) =>
  state.reminders?.reminders || [];

export const selectDueReminders = createSelector(
  [selectAllReminders],
  (reminders) =>
    reminders.filter((reminder) => {
      const isSent = reminder?.is_sent ?? reminder?.isSent;
      const remindAt = reminder?.remind_at ?? reminder?.remindAt;

      return (
        !isSent &&
        remindAt &&
        dayjs(remindAt).isBefore(dayjs().add(1, "day"), "day")
      );
    })
);

export const selectUpcomingReminders = createSelector(
  [selectAllReminders],
  (reminders) =>
    reminders.filter((reminder) => {
      const isSent = reminder?.is_sent ?? reminder?.isSent;
      const remindAt = reminder?.remind_at ?? reminder?.remindAt;

      return (
        !isSent &&
        remindAt &&
        dayjs(remindAt).isAfter(dayjs().subtract(1, "day"), "day") &&
        dayjs(remindAt).isBefore(dayjs().add(8, "day"), "day")
      );
    })
);

export const selectReminderById = createSelector(
  [selectAllReminders, (_, id) => id],
  (reminders, id) =>
    reminders.find((reminder) => String(reminder?.id) === String(id)) || null
);

/* ========================= STATE ========================= */

const initialState = {
  reminders: [],
  byUid: {},
  selectedReminder: null,
  status: "idle",
  error: null,
};

/* ========================= SLICE ========================= */

const remindersSlice = createSlice({
  name: "reminders",
  initialState,
  reducers: {
    patchReminderLocal: (state, action) => {
      const { id, changes } = action.payload || {};
      if (!id) return;

      const patch = changes || {};

      const flatIndex = state.reminders.findIndex(
        (item) => String(item?.id) === String(id)
      );

      if (flatIndex !== -1) {
        state.reminders[flatIndex] = {
          ...state.reminders[flatIndex],
          ...patch,
        };
      }

      Object.keys(state.byUid || {}).forEach((uid) => {
        const index = (state.byUid[uid] || []).findIndex(
          (item) => String(item?.id) === String(id)
        );

        if (index !== -1) {
          state.byUid[uid][index] = {
            ...state.byUid[uid][index],
            ...patch,
          };
        }
      });

      if (String(state.selectedReminder?.id) === String(id)) {
        state.selectedReminder = {
          ...state.selectedReminder,
          ...patch,
        };
      }
    },

    markSentLocal: (state, action) => {
      const id = action.payload;
      if (!id) return;

      const patch = {
        is_sent: true,
        isSent: true,
      };

      const flatIndex = state.reminders.findIndex(
        (item) => String(item?.id) === String(id)
      );

      if (flatIndex !== -1) {
        state.reminders[flatIndex] = {
          ...state.reminders[flatIndex],
          ...patch,
        };
      }

      Object.keys(state.byUid || {}).forEach((uid) => {
        const index = (state.byUid[uid] || []).findIndex(
          (item) => String(item?.id) === String(id)
        );

        if (index !== -1) {
          state.byUid[uid][index] = {
            ...state.byUid[uid][index],
            ...patch,
          };
        }
      });
    },

    removeReminderLocal: (state, action) => {
      const id = action.payload;

      state.reminders = state.reminders.filter(
        (item) => String(item?.id) !== String(id)
      );

      Object.keys(state.byUid || {}).forEach((uid) => {
        state.byUid[uid] = (state.byUid[uid] || []).filter(
          (item) => String(item?.id) !== String(id)
        );
      });

      if (String(state.selectedReminder?.id) === String(id)) {
        state.selectedReminder = null;
      }
    },

    setSelectedReminder: (state, action) => {
      state.selectedReminder = action.payload || null;
    },

    clearSelectedReminder: (state) => {
      state.selectedReminder = null;
    },

    clearRemindersError: (state) => {
      state.error = null;
    },

    resetRemindersStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },

    clearReminders: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReminders.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchReminders.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        const uid = String(action.meta.arg);

        state.byUid[uid] = Array.isArray(action.payload)
          ? action.payload.map((item) =>
            normalizeReminder(item, uid)
          )
          : [];

        syncFlatReminders(state);

      })
      .addCase(fetchReminders.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "fetchReminders failed";
      })

      .addCase(createReminders.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createReminders.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;

        const {
          uid,
          reminders,
        } = action.payload || {};

        if (!uid || !Array.isArray(reminders)) {
          return;
        }

        const key = String(uid);

        state.byUid[key] = reminders.map((item) =>
          normalizeReminder(item, key)
        );

        syncFlatReminders(state);
      })
      .addCase(createReminders.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "createReminders failed";
      })

      .addCase(updateReminder.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateReminder.fulfilled, (state, action) => {
        state.status = "succeeded";

        const updated = action.payload?.reminder;
        if (!updated?.id) return;

        const id = String(updated.id);

        Object.keys(state.byUid || {}).forEach((uid) => {
          const index = (state.byUid[uid] || []).findIndex(
            (item) => String(item?.id) === id
          );

          if (index !== -1) {
            state.byUid[uid][index] = {
              ...state.byUid[uid][index],
              ...updated,
            };
          }
        });

        const uid = updated.uid ?? action.payload?.uid;
        if (uid != null) {
          const key = String(uid);
          if (!state.byUid[key]) state.byUid[key] = [];
          upsertReminder(state.byUid[key], normalizeReminder(updated, key));
        }

        if (String(state.selectedReminder?.id) === id) {
          state.selectedReminder = {
            ...state.selectedReminder,
            ...updated,
          };
        }

        syncFlatReminders(state);
      })
      .addCase(updateReminder.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "updateReminder failed";
      })

      .addCase(deleteReminder.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteReminder.fulfilled, (state, action) => {
        state.status = "succeeded";

        const id = String(action.payload?.id);

        Object.keys(state.byUid || {}).forEach((uid) => {
          state.byUid[uid] = (state.byUid[uid] || []).filter(
            (item) => String(item?.id) !== id
          );
        });

        if (String(state.selectedReminder?.id) === id) {
          state.selectedReminder = null;
        }

        syncFlatReminders(state);
      })
      .addCase(deleteReminder.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "deleteReminder failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ========================= ACTIONS ========================= */

export const {
  patchReminderLocal,
  markSentLocal,
  removeReminderLocal,
  setSelectedReminder,
  clearSelectedReminder,
  clearRemindersError,
  resetRemindersStatus,
  clearReminders,
} = remindersSlice.actions;

export default remindersSlice.reducer;